/**
 * Regular (Equilateral) Octagon / Hexagon Cushion Configurator
 * uscushion.com · 2026-08
 *
 * TRUE regular polygons — all sides equal. Customer enters ONE dimension:
 *   Side Length (one edge) + Thickness. Everything else is derived.
 *   Hexagon = 6 equal sides (flat top/bottom, points left/right).
 *   Octagon = 8 equal sides (flat top/bottom/left/right + 4 corners).
 * Pricing: bounding-box (overall Width × overall Depth) — foam & fabric cut from the
 *   rectangle, offcuts wasted. Same engine/口径 as the Round configurator.
 * formula_type: `octagon` / `hexagon` (or `polygon` to expose both). Shares #cc-configurator.
 */
(function () {
  'use strict';

  /* ─── Cost constants (same as bench / round) ─── */
  var FABRIC_PRICE_RMB  = 25;
  var FABRIC_RATE_RMB   = { marine: 54 };
  function fabricRate() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return FABRIC_RATE_RMB[g] || FABRIC_PRICE_RMB;
  }
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MARINE_COST_RATIO = 0.38;
  var COVER_COST_RATIO  = 0.55;   /* Extra covers = 45% margin (AOV add-on, lower than cushion's 60/62%); same for standard & marine */
  function targetRatio() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return g === 'marine' ? MARINE_COST_RATIO : TARGET_COST_RATIO;
  }
  var MIN_PRICE         = 45;

  /* ─── Addon pricing (USD) ─── */
  var ADDON = {
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };
  /* ─── Units (depolster.de): inputs in cm, pricing engine unchanged (inch). inch→cm only for display. ─── */
  var CM_PER_IN = 2.54;
  function parseNum(v) { return parseFloat(String(v == null ? '' : v).replace(',', '.')) || 0; }
  function round1(n) { return Math.round(n * 10) / 10; }
  var PILLOW_SIZE = { '12x20': '30×50 cm', '16x16': '40×40 cm', '18x18': '45×45 cm', '20x20': '50×50 cm', '24x24': '60×60 cm', 'custom': 'Sondermaß' };
  function pillowLabel(v) { var m = /^(\d)pcs_(.+)$/.exec(v); return m ? (m[1] + ' Stück ' + (PILLOW_SIZE[m[2]] || m[2])) : v; }
  var ARMREST_LABEL = { square_2pcs: 'Eckig — 2 Stück', round_2pcs: 'Rund — 2 Stück' };


  var LABELS = {
    none: 'Keine', single: 'Einfacher Keder', double: 'Doppelter Keder',
    match: 'Passend zum Stoff', white: 'Weißer Keder', black: 'Schwarzer Keder', navy: 'Marineblauer Keder', cream: 'Cremefarbener Keder',
    two: '2 Bänder', four: '4 Bänder',
    regular: 'Standard-Reißverschluss', invisible: 'Nahtverdeckter Reißverschluss',
    velcro: 'Klettband', black_pad: 'Schwarze Unterlage', beige_pad: 'Beige Unterlage',
    hexagon: 'Hexagon', octagon: 'Octagon'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  /* ─── State ─── */
  var state = {
    shape: 'octagon',
    side: 0, thickness: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* ─── Geometry: TRUE regular polygon from side length (inch, center origin, y-down, flat-top) ─── */
  function polyRegular(shape, side) {
    var n = (shape === 'hexagon') ? 6 : 8;
    var R = side / (2 * Math.sin(Math.PI / n));          // circumradius from side
    var off = (shape === 'hexagon') ? 0 : (Math.PI / n); // hexagon: points left/right; octagon: flat sides
    var v = [];
    for (var k = 0; k < n; k++) {
      var a = off + k * (2 * Math.PI / n);
      v.push([R * Math.cos(a), R * Math.sin(a)]);
    }
    return v;
  }
  function bounds(verts) {
    var mx = 0, my = 0, i;
    for (i = 0; i < verts.length; i++) { mx = Math.max(mx, Math.abs(verts[i][0])); my = Math.max(my, Math.abs(verts[i][1])); }
    return { w: 2 * mx, h: 2 * my, mx: mx, my: my };
  }

  /* ─── Mini flat-view SVG for the shape-selector cards ─── */
  function miniPoly(shape) {
    var v = polyRegular(shape, 10), b = bounds(v), i, pts = [];
    var sc = Math.min(50 / b.w, 36 / b.h);
    for (i = 0; i < v.length; i++) pts.push((30 + v[i][0] * sc).toFixed(1) + ',' + (22 + v[i][1] * sc).toFixed(1));
    return '<svg viewBox="0 0 60 44" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">'
      + '<polygon points="' + pts.join(' ') + '" fill="#EDE9E0" stroke="#2A2A28" stroke-width="1.8" stroke-linejoin="round"/></svg>';
  }

  /* ─── Pricing (bounding-box overall Width × Depth — same as Round) ─── */
  function calcPolyPrice(shape, side, T) {
    if (!side || !T) return null;
    var b = bounds(polyRegular(shape, side));
    var Wm = b.w / 39.37, Lm = b.h / 39.37, Tm = T / 39.37;
    var fabricArea = 2 * Wm * Lm + 2 * (Wm + Lm) * Tm;
    var foamVolume = Wm * Lm * Tm;
    var totalWeight = foamVolume * FOAM_DENSITY + fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * fabricRate() + foamVolume * FOAM_PRICE_RMB
      + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.max(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / targetRatio() * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
  }

  /* One extra cover = a fabric-only shell (no foam). Uses the polygon's bounding
     rectangle (bounds(polyRegular(shape, side))), same basis as calcPolyPrice.
     Fabric grade auto via fabricRate, at COVER_COST_RATIO (45% margin). */
  function extraCoverUnit() {
    var side = state.side, T = state.thickness;
    if (!side || !T) return 0;
    var b = bounds(polyRegular(state.shape, side));
    var Wm = b.w / 39.37, Lm = b.h / 39.37, Tm = T / 39.37;
    var fabricArea = 2 * Wm * Lm + 2 * (Wm + Lm) * Tm;
    var weight = fabricArea * FABRIC_GSM / 1000; // fabric weight only — no foam
    var totalRMB = fabricArea * fabricRate() + weight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.round(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / COVER_COST_RATIO);
  }

  function updateExtraCoverLabels() {
    if (!els.extraCovers) return;
    var unit = extraCoverUnit();
    var opts = els.extraCovers.options;
    for (var i = 0; i < opts.length; i++) {
      var n = parseInt(opts[i].value) || 0;
      if (n === 0) { opts[i].text = 'Kein zusätzlicher Bezug'; continue; }
      var label = (n === 1 ? '1 Set' : n + ' Sets');
      opts[i].text = unit > 0 ? label + ' (+' + (unit * n) + ' €)' : label;
    }
  }

  function getAddonTotal() {
    return (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + extraCoverUnit() * (parseInt(state.extraCovers) || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0)
      + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function hasDims() { return state.side >= 3 && state.thickness >= 1; }

  function isComplete() {
    return hasDims() && state.fabric && state.piping && state.straps && state.antislip;
  }

  /* ═══════════════════════════════════════════════════════════
     SVG Preview — true regular polygon top face + extruded side band
     viewBox 440×300, center [210,120]
     ═══════════════════════════════════════════════════════════ */
  function drawCushion() {
    var svg = els.dimSvg;
    if (!svg) return;

    var S = state.sidecm, T = state.thicknesscm;
    var empty = (!S);

    function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }
    function pt(a) { return a[0].toFixed(1) + ',' + a[1].toFixed(1); }
    function f(n)  { return n.toFixed(1); }

    /* ── Fabric colors ── */
    var topC = '#EDE9E0', frontC = '#D5D0C2', strokeC = '#2A2A28', fabricLum = 200;
    if (state.fabric && state.fabric.tags) {
      var colorTag = extractTag(state.fabric.tags, 'color');
      var baseHex = FABRIC_COLORS[colorTag];
      if (baseHex) {
        topC = lighten(baseHex, 0.50); frontC = lighten(baseHex, 0.20); strokeC = darken(baseHex, 0.45);
        var sc0 = hexToRgb(strokeC);
        if ((sc0[0] + sc0[1] + sc0[2]) / 3 > 120) strokeC = '#2A2A28';
        var fc = hexToRgb(baseHex); fabricLum = (fc[0] + fc[1] + fc[2]) / 3;
      }
    }

    var h = '';
    h += '<defs><marker id="ply-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';

    var uS = S || 18;
    var verts = polyRegular(state.shape, uS), b = bounds(verts), i;
    var cx = 210, cy = 120;
    var sc = clamp(200 / Math.max(b.w, b.h), 2, 11);
    var halfWpx = b.mx * sc, halfHpx = b.my * sc;
    var thickPx = clamp((T || 3) * 6, 8, 44);

    var topPts = [], botPts = [];
    for (i = 0; i < verts.length; i++) {
      var x = cx + verts[i][0] * sc, y = cy + verts[i][1] * sc;
      topPts.push([x, y]); botPts.push([x, y + thickPx]);
    }

    var visIdx = [], zipIdx = [];
    for (i = 0; i < topPts.length; i++) {
      if (topPts[i][1] >= cy - 0.5) visIdx.push(i);
      if (topPts[i][1] <= cy + 0.5) zipIdx.push(i);
    }
    visIdx.sort(function (a, b) { return topPts[a][0] - topPts[b][0]; });
    zipIdx.sort(function (a, b) { return topPts[a][0] - topPts[b][0]; });

    /* ── Straps ── */
    if ((state.straps === 'two' || state.straps === 'four') && !empty && visIdx.length > 0) {
      var tieC = fabricLum < 100 ? '#8A8A85' : '#4A4A47', hangLen = 15;
      function drawTie(bx, by, dir) {
        var spread = 4;
        h += '<path d="M' + f(bx - spread * 0.5) + ' ' + f(by) + ' Q' + f(bx - spread + dir * 3) + ' ' + f(by + hangLen * 0.5) + ' ' + f(bx - spread * 1.5 + dir * 5) + ' ' + f(by + hangLen) + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
        h += '<path d="M' + f(bx + spread * 0.5) + ' ' + f(by) + ' Q' + f(bx + spread + dir * 2) + ' ' + f(by + hangLen * 0.5) + ' ' + f(bx + spread * 1.5 + dir * 4) + ' ' + f(by + hangLen) + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
      }
      var midI = visIdx[Math.floor(visIdx.length / 2)];
      drawTie(botPts[midI][0], botPts[midI][1], 0);
      if (state.straps === 'four' && visIdx.length > 2) {
        drawTie(botPts[visIdx[0]][0], botPts[visIdx[0]][1], -1);
        drawTie(botPts[visIdx[visIdx.length - 1]][0], botPts[visIdx[visIdx.length - 1]][1], 1);
      }
    }

    /* ── Side band ── */
    if (visIdx.length > 0) {
      var sidePath = 'M ' + pt(topPts[visIdx[0]]);
      for (i = 1; i < visIdx.length; i++) sidePath += ' L ' + pt(topPts[visIdx[i]]);
      for (i = visIdx.length - 1; i >= 0; i--) sidePath += ' L ' + pt(botPts[visIdx[i]]);
      sidePath += ' Z';
      h += '<path d="' + sidePath + '" fill="' + frontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
      var botEdge = 'M ' + pt(botPts[visIdx[0]]);
      for (i = 1; i < visIdx.length; i++) botEdge += ' L ' + pt(botPts[visIdx[i]]);
      h += '<path d="' + botEdge + '" fill="none" stroke="' + strokeC + '" stroke-width="1.4"/>';
    }

    /* ── Top face ── */
    var topPath = 'M ' + pt(topPts[0]);
    for (i = 1; i < topPts.length; i++) topPath += ' L ' + pt(topPts[i]);
    topPath += ' Z';
    h += '<path d="' + topPath + '" fill="' + topC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    /* ── Zipper: rear (top) edges, green ── */
    if (zipIdx.length > 1) {
      var zipPath = 'M ' + pt(topPts[zipIdx[0]]);
      for (i = 1; i < zipIdx.length; i++) zipPath += ' L ' + pt(topPts[zipIdx[i]]);
      h += '<path d="' + zipPath + '" fill="none" stroke="#3A7D2C" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" opacity="0.92"/>';
      var zt = topPts[zipIdx[Math.floor(zipIdx.length / 2)]];
      h += '<text x="' + f(zt[0]) + '" y="' + f(zt[1] - 5) + '" text-anchor="middle" font-size="8.5" font-family="Poppins,sans-serif" font-weight="600" fill="#3A7D2C">zipper</text>';
    }

    /* ── Piping ── */
    if (state.piping === 'single' || state.piping === 'double') {
      var pipC = '#F5F1E8', pw = 2.2;
      h += '<path d="' + topPath + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" opacity="0.85"/>';
      if (state.piping === 'double' && visIdx.length > 0) {
        var botPip = 'M ' + pt(botPts[visIdx[0]]);
        for (i = 1; i < visIdx.length; i++) botPip += ' L ' + pt(botPts[visIdx[i]]);
        h += '<path d="' + botPip + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linecap="round" opacity="0.85"/>';
      }
    }

    /* ── Anti-slip strip ── */
    if (state.antislip && state.antislip !== 'none' && !empty && visIdx.length > 0) {
      var padC, padOp;
      if (state.antislip === 'black_pad') { padC = '#1A1A18'; padOp = 0.32; }
      else if (state.antislip === 'beige_pad') { padC = '#C8B898'; padOp = 0.4; }
      else { padC = '#4A4020'; padOp = 0.26; }
      var stripH = 3, stripPath = 'M ' + pt(botPts[visIdx[0]]);
      for (i = 1; i < visIdx.length; i++) stripPath += ' L ' + pt(botPts[visIdx[i]]);
      for (i = visIdx.length - 1; i >= 0; i--) { var bp = botPts[visIdx[i]]; stripPath += ' L ' + pt([bp[0], bp[1] + stripH]); }
      stripPath += ' Z';
      h += '<path d="' + stripPath + '" fill="' + padC + '" opacity="' + padOp + '"/>';
    }

    /* ── Per-edge labels — every side equals S (shows it's equilateral) ── */
    if (!empty) {
      for (i = 0; i < verts.length; i++) {
        var jj = (i + 1) % verts.length;
        var mx = (topPts[i][0] + topPts[jj][0]) / 2, my = (topPts[i][1] + topPts[jj][1]) / 2;
        var vx = mx - cx, vy = my - cy, vl = Math.hypot(vx, vy) || 1;
        h += '<text x="' + f(mx + (vx / vl) * 9) + '" y="' + f(my + (vy / vl) * 9 + 3) + '" text-anchor="middle" font-size="8" font-family="Poppins,sans-serif" fill="#8A6A3A">' + S.toFixed(1) + '&#8243;</text>';
      }
    }

    /* ── Only the Thickness arrow (side length is labelled on every edge above; no overall W/D) ── */
    var thkC = '#3d5e3f', ls = 10;
    var thkPt = topPts[visIdx.length ? visIdx[visIdx.length - 1] : 0], thkBt = botPts[visIdx.length ? visIdx[visIdx.length - 1] : 0], hug = 16;
    h += '<line x1="' + f(thkPt[0]) + '" y1="' + f(thkPt[1]) + '" x2="' + f(thkPt[0] + hug) + '" y2="' + f(thkPt[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thkBt[0]) + '" y1="' + f(thkBt[1]) + '" x2="' + f(thkBt[0] + hug) + '" y2="' + f(thkBt[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thkPt[0] + hug) + '" y1="' + f(thkPt[1] + 2) + '" x2="' + f(thkBt[0] + hug) + '" y2="' + f(thkBt[1] - 2) + '" stroke="' + thkC + '" stroke-width="1.3" marker-start="url(#ply-arr)" marker-end="url(#ply-arr)"/>';
    h += '<text x="' + f(thkPt[0] + hug + 5) + '" y="' + f((thkPt[1] + thkBt[1]) / 2 + 4) + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + thkC + '">' + (T ? 'H ' + T + ' cm' : 'Thick') + '</text>';

    /* Side Length caption under the shape */
    if (!empty) h += '<text x="' + f(cx) + '" y="' + f(cy + halfHpx + thickPx + 20) + '" text-anchor="middle" font-size="10" font-family="Poppins,sans-serif" font-weight="600" fill="#8A6A3A">Seitenlänge ' + S + ' cm (alle gleich)</text>';
    else h += '<text x="' + f(cx) + '" y="' + f(cy + halfHpx + thickPx + 24) + '" text-anchor="middle" font-size="11" font-family="Poppins,sans-serif" font-weight="500" fill="#888">Enter the side length to preview your cushion</text>';

    svg.innerHTML = h;
  }

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var parts = [LABELS[state.shape] || state.shape];
    if (hasDims()) parts.push(round1(state.sidecm) + ' cm Seite · ' + round1(state.thicknesscm) + ' cm dick');
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') {
      var pl = LABELS[state.piping];
      if (state.pipingColor && state.pipingColor !== 'match') pl += ' (' + (LABELS[state.pipingColor] || state.pipingColor) + ')';
      parts.push(pl);
    }
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    els.summary.textContent = parts.join(' · ');
  }

  /* ─── Recalc ─── */
  function recalc() {
    var _cf = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (_cf && _cf !== state.fabric) state.fabric = _cf;
    state.sidecm = parseNum(els.dimSide.value); state.side = state.sidecm / CM_PER_IN;
    state.thicknesscm = parseNum(els.dimT.value); state.thickness = state.thicknesscm / CM_PER_IN;
    state.extraCovers = els.extraCovers ? els.extraCovers.value : '0';
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes ? els.notes.value : '';

    if (els.pipingColorWrap) {
      var show = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = show ? '' : 'none';
      if (!show) { state.pipingColor = 'match'; if (els.pipingColor) els.pipingColor.value = 'match'; }
    }

    ['dimSide', 'dimT'].forEach(function (id) {
      if (els[id]) els[id].classList.toggle('ok', parseFloat(els[id].value) > 0);
    });

    drawCushion();
    updateSummary();

    var dimsOk = hasDims();
    var ec = parseInt(state.extraCovers) || 0;
    updateExtraCoverLabels();
    var price = dimsOk ? calcPolyPrice(state.shape, state.side, state.thickness) : null;
    var addonTotal = getAddonTotal();

    if (price !== null) {
      els.priceMain.textContent = Math.round(price + addonTotal) + ' €';
      var parts = ['Grundpreis ' + Math.round(price) + ' €'];
      if (ADDON.piping[state.piping])     parts.push('Keder +' + ADDON.piping[state.piping] + ' €');
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Keder-Farbe +' + ADDON.pipingColor[state.pipingColor] + ' €');
      if (ADDON.straps[state.straps])     parts.push('Bänder +' + ADDON.straps[state.straps] + ' €');
      if (ADDON.zipper[state.zipper])     parts.push('Reißverschluss +' + ADDON.zipper[state.zipper] + ' €');
      if (ADDON.antislip[state.antislip]) parts.push('Anti-Rutsch +' + ADDON.antislip[state.antislip] + ' €');
      if (ec > 0)                         parts.push('Zusätzliche Bezüge +' + (extraCoverUnit() * ec) + ' €');
      if (ADDON.throwPillow[state.throwPillow])   parts.push('Dekokissen +' + ADDON.throwPillow[state.throwPillow] + ' €');
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('Kissenbezüge +' + ADDON.pillowCovers[state.pillowCovers] + ' €');
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armlehnenkissen +' + ADDON.armrestPillow[state.armrestPillow] + ' €');
      els.priceBreak.textContent = parts.join(' · ');
    } else {
      els.priceMain.textContent = '— €';
      els.priceBreak.textContent = 'Seitenlänge eingeben, um den Preis zu sehen';
    }

    var complete = isComplete();
    [els.addBtn, els.addBtnSticky].filter(Boolean).forEach(function (btn) {
      btn.disabled = !complete;
      btn.textContent = (complete && price !== null) ? 'Zur Bestellung hinzufügen — ' + Math.round(price + addonTotal) + ' € · Kostenloser Versand →' : 'Zur Bestellung hinzufügen →';
    });

    if (els.addNote) {
      if (complete) els.addNote.textContent = 'Kostenloser Versand · Versand in ca. 20 Werktagen';
      else {
        var missing = [];
        if (!dimsOk) missing.push('Seitenlänge & Höhe');
        if (!state.fabric) missing.push('fabric');
        if (!state.piping) missing.push('piping');
        if (!state.straps) missing.push('straps');
        if (!state.antislip) missing.push('anti-slip');
        els.addNote.textContent = missing.length ? missing.join(', ') + ' auswählen, um fortzufahren' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: { price: price !== null ? Math.round(price + addonTotal) : null, state: state }
    }));
  }

  /* ─── Card selection ─── */
  function pickCard(el, group, value) {
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    recalc();
  }

  /* ─── Line properties for cart ─── */
  function getLineProperties() {
    var ec = parseInt(state.extraCovers) || 0;
    var b = bounds(polyRegular(state.shape, state.side));
    var price = calcPolyPrice(state.shape, state.side, state.thickness);
    var finalPrice = Math.round(price + getAddonTotal());
    var props = {
      'Form': (LABELS[state.shape] || state.shape) + ' (' + (state.shape === 'hexagon' ? '6' : '8') + ' gleiche Seiten)',
      'Seitenlänge': round1(state.sidecm) + ' cm',
      'Gesamtbreite': round1(b.w * CM_PER_IN) + ' cm',
      'Gesamttiefe': round1(b.h * CM_PER_IN) + ' cm',
      'Höhe': round1(state.thicknesscm) + ' cm',
      '_Maße (inch)': round1(state.side) + ' (Seite) × ' + round1(state.thickness) + ' (Höhe)',
      'Keder': LABELS[state.piping] || state.piping,
      'Keder-Farbe': (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Passend zum Stoff',
      'Bänder': LABELS[state.straps] || state.straps,
      'Reißverschluss': LABELS[state.zipper] || state.zipper,
      'Anti-Rutsch': LABELS[state.antislip] || state.antislip
    };
    if (state.fabric) {
      props['Stoff'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
      var _grade = extractTag(state.fabric.tags || [], 'grade');
      if (_grade) props['Stoffqualität'] = _grade.charAt(0).toUpperCase() + _grade.slice(1);
    }
    if (ec > 0) props['Zusätzliche Bezüge'] = ec + (ec === 1 ? ' Set' : ' Sets');
    if (state.throwPillow !== 'none') props['Dekokissen'] = pillowLabel(state.throwPillow);
    if (state.pillowCovers !== 'none') props['Kissenbezüge'] = pillowLabel(state.pillowCovers);
    if (state.armrestPillow !== 'none') props['Armlehnenkissen'] = ARMREST_LABEL[state.armrestPillow] || state.armrestPillow;
    if (state.zipperPlacement) props['Reißverschluss-Position'] = state.zipperPlacement;
    if (state.notes.trim()) props['Anmerkung'] = state.notes.trim();
    props['_Source Product'] = (document.getElementById('cc-configurator') || { dataset: {} }).dataset.productTitle || document.title.split('–')[0].split('—')[0].trim() || location.pathname;

    var apiPayload = {
      shape: state.shape,
      pieces: [{ side: state.side, overallWidth: +b.w.toFixed(1), overallDepth: +b.h.toFixed(1), thickness: state.thickness }],
      piping: state.piping, pipingColor: state.pipingColor, straps: state.straps,
      zipper: state.zipper, antislip: state.antislip,
      extraCovers: ec, throwPillow: state.throwPillow, pillowCovers: state.pillowCovers, armrestPillow: state.armrestPillow
    };
    return { properties: props, price: finalPrice, apiPayload: apiPayload };
  }

  /* ─── DOM transformation (adapt shared Liquid section) ─── */
  function transformDOM() {
    /* One dimension only → hide Width, hide the entire back-dims row + labels */
    var backDims = document.querySelectorAll('.cc-back-dims');
    for (var i = 0; i < backDims.length; i++) backDims[i].style.display = 'none';
    var seatLabel = document.querySelector('.cc-seat-label');
    if (seatLabel) seatLabel.style.display = 'none';

    /* Relabel L → Side Length; hide W */
    var labelL = document.querySelector('label[for="cc-dim-l"]');
    if (labelL) labelL.innerHTML = 'Seitenlänge (cm) <span class="cc-req">*</span>';
    var labelT = document.querySelector('label[for="cc-dim-t"]');
    if (labelT) labelT.innerHTML = 'Höhe (cm) <span class="cc-req">*</span>';
    var inputW = document.getElementById('cc-dim-w');
    if (inputW) { var wWrap = inputW.closest('.cc-field'); if (wWrap) wWrap.style.display = 'none'; }
    var inL = document.getElementById('cc-dim-l');
    if (inL) inL.setAttribute('placeholder', 'z. B. 12');

    /* Make the seat dims row 2 columns (Side + Thickness) */
    var dimsGrid = document.querySelector('.cc-sp-dims:not(.cc-back-dims)');
    if (dimsGrid) dimsGrid.style.gridTemplateColumns = '1fr 1fr';

    /* Shape grid → BOTH Octagon & Hexagon always selectable; formula_type only sets the default */
    var shapeGrid = document.getElementById('cc-shape-grid');
    if (shapeGrid) {
      function card(val, name, sel) {
        return '<div class="cc-opt-card' + (sel ? ' sel' : '') + '" data-group="shape" data-value="' + val + '">'
          + '<div class="cc-opt-img" style="background:#F5F1E8">' + miniPoly(val) + '</div>'
          + '<div class="cc-opt-name">' + name + '</div><div class="cc-opt-price">inklusive</div></div>';
      }
      shapeGrid.innerHTML = card('octagon', 'Octagon', state.shape === 'octagon') + card('hexagon', 'Hexagon', state.shape === 'hexagon');
    }

    var svg = document.getElementById('cc-dim-svg');
    if (svg) { svg.setAttribute('viewBox', '0 0 440 300'); svg.style.maxHeight = '210px'; }
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;
    var ft = (wrap.getAttribute('data-formula-type') || '').replace('_cover', '');
    if (ft && ft !== 'polygon' && ft !== 'hexagon' && ft !== 'octagon') return;
    /* formula_type just sets the DEFAULT shape; both shapes stay selectable */
    if (ft === 'hexagon' || ft === 'octagon') state.shape = ft;

    transformDOM();

    els.priceMain    = document.getElementById('cc-price-main');
    els.priceBreak   = document.getElementById('cc-price-break');
    els.summary      = document.getElementById('cc-sp-summary');
    els.dimSide      = document.getElementById('cc-dim-l');
    els.dimT         = document.getElementById('cc-dim-t');
    els.extraCovers  = document.getElementById('cc-extra-covers');
    els.throwPillow  = document.getElementById('cc-throw-pillow');
    els.pillowCovers = document.getElementById('cc-pillow-covers');
    els.armrestPillow = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement = document.getElementById('cc-zipper-placement');
    els.pipingColor    = document.getElementById('cc-piping-color');
    els.pipingColorWrap = document.getElementById('cc-piping-color-wrap');
    els.notes        = document.getElementById('cc-notes');
    els.addBtn       = document.getElementById('cc-add-btn');
    els.addBtnSticky = document.getElementById('cc-add-btn-sticky');
    els.addNote      = document.getElementById('cc-add-note');
    els.dimSvg       = document.getElementById('cc-dim-svg');

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('.cc-opt-card');
      if (!card || !card.dataset.group) return;
      pickCard(card, card.dataset.group, card.dataset.value);
    });

    ['dimSide', 'dimT'].forEach(function (id) {
      if (els[id]) els[id].addEventListener('input', recalc);
    });
    if (els.extraCovers) els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    if (els.notes) els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) { state.fabric = e.detail; recalc(); });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) { state.fabric = existingFabric; recalc(); }
    setInterval(function () {
      var fb = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (fb && fb !== state.fabric) { state.fabric = fb; recalc(); }
    }, 800);

    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');

    drawCushion();
    recalc();
  }

  window.PolygonCushionConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();